/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, PrimaryButton } from '@fluentui/react/lib/Button';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { routeTable } from 'src/core/utils/routeTable';

export type HomePageHeaderProps = {
    /**
     * Whether the user is signed in or not.
     */
    isSignedIn: boolean;

    /**
     * A callback to sign in the user.
     */
    onSignIn: () => void;
};

const messages = defineMessages({
    title: {
        id: 'portal.homePageHeader.title',
        defaultMessage: 'Language Understanding (LUIS)'
    },
    subTitle: {
        id: 'portal.homePageHeader.subTitle',
        defaultMessage: `A machine learning-based service to build natural language into apps, bots,
and IoT devices. Quickly create enterprise-ready, custom models that continuously improve.`
    },
    logIn: {
        id: 'portal.homePageHeader.logIn',
        defaultMessage: 'Login / Sign up'
    },
    goToApps: {
        id: 'portal.homePageHeader.goToApps',
        defaultMessage: 'Go to apps'
    },
    headerImageAlt: {
        id: 'portal.homePageHeader.headerImageAlt',
        defaultMessage: 'LUIS Header'
    }
});

const { styleComponent, getClassName } = getStylist('HomePageHeader');

const HeaderContainer = styleComponent<IStackProps>(Stack)<{ isLargeScreen: boolean }>(
    'HomePageHeaderContainer',
    theme => ({
        position: 'relative',
        backgroundColor: theme.headerBackgroundColor,
        height: theme.headerHeight,
        paddingLeft: theme.headerPaddingLargeScreen,
        paddingRight: theme.headerPaddingLargeScreen
    }),
    ({ customProps }, theme) => ({
        paddingLeft: customProps.isLargeScreen ? theme.headerPaddingLargeScreen : theme.headerPaddingSmallScreen,
        paddingRight: customProps.isLargeScreen ? theme.headerPaddingLargeScreen : theme.headerPaddingSmallScreen
    })
);

const headerImageClassName = getClassName('HomePageHeaderImage', {
    position: 'absolute',
    height: '100%',
    top: 0,
    bottom: 0,
    right: 0,
    opacity: '0.8'
});

const HeaderTitle = styleComponent<LuisTextProps>(LuisText)<{ isLargeScreen: boolean }>(
    'HomePageHeaderTitle',
    theme => ({ position: 'relative', color: theme.textColor }),
    ({ customProps }, theme) => ({ fontSize: customProps.isLargeScreen ? theme.titleFontSizeLargeScreen : theme.titleFontSizeSmallScreen })
);

const HeaderSubTitle = styleComponent<LuisTextProps>(LuisText)<{ isLargeScreen: boolean }>(
    'HomePageHeaderSubTitle',
    theme => ({
        position: 'relative',
        color: theme.textColor,
        marginBottom: theme.subtitleMarginBottom,
        backgroundColor: 'rgba(92,18,107,0.9)',
        borderRadius: '18px',
        padding: '5px'
    }),
    ({ customProps }, theme) => ({ width: customProps.isLargeScreen ? theme.subTitleWidthLargeScreen : theme.subTitleWidthSmallScreen })
);

const HeaderButton = styleComponent<IButtonProps>(PrimaryButton)('HomePageSignInButton', theme => ({ width: theme.buttonWidth }));

/**
 * Renders the homepage header with the title and the
 * header graphics.
 */
export const HomePageHeader = (props: HomePageHeaderProps) => {
    const { isSignedIn, onSignIn } = props;
    const { localize } = useLocalization();
    const history = useHistory();
    const { isXLargeOrGreater } = useResponsiveMode();

    return (
        <HeaderContainer verticalAlign="center" as="header" customProps={{ isLargeScreen: isXLargeOrGreater }}>
            <img
                className={headerImageClassName}
                alt={localize(messages.headerImageAlt)}
                src={getAssetsUrl('assets/images/homePage/luisHeader.png')}
            />

            <HeaderTitle as="h1" lineHeight="70px" block customProps={{ isLargeScreen: isXLargeOrGreater }}>
                {localize(messages.title)}
            </HeaderTitle>

            <HeaderSubTitle variant="xLarge" as="h2" block customProps={{ isLargeScreen: isXLargeOrGreater }}>
                {localize(messages.subTitle)}
            </HeaderSubTitle>

            {isSignedIn ? (
                <HeaderButton onClick={() => history.push(routeTable.luis.applicationsPage())}>{localize(messages.goToApps)}</HeaderButton>
            ) : (
                <HeaderButton onClick={onSignIn}>{localize(messages.logIn)}</HeaderButton>
            )}
        </HeaderContainer>
    );
};
